import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectsData } from '../../data/projectsData';
import styles from './Section4.module.scss';

const Section4: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  
  // 스크롤 진행률 상태 관리를 위한 ref
  const scrollProgressRef = useRef<number>(0);
  
  // 컨테이너의 상태 관리 (상단, 고정, 하단)
  const [containerState, setContainerState] = useState<'top' | 'fixed' | 'bottom'>('top');
  
  // 스크롤 진행률 상태 추가
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  // 화면 크기에 따른 갤러리 조정을 위한 상태
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(false);
  
  // 드래그 관련 상태 추가
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startX, setStartX] = useState<number>(0);
  const [startY, setStartY] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const lastDragX = useRef<number>(0);
  const lastDragTime = useRef<number>(0);
  
  // 드래그 방향 감지를 위한 상태
  const [dragDirection, setDragDirection] = useState<'none' | 'horizontal' | 'vertical'>('none');
  const directionLockTimerRef = useRef<number | null>(null);
  
  // 관성 스크롤을 위한 참조
  const momentumRef = useRef<number | null>(null);
  const velocityRef = useRef<number>(0);

  // 드래그 관련 상수
  const DIRECTION_THRESHOLD = 8; // 8px 이상 움직여야 방향 결정 (더 민감하게 조정)
  const DIRECTION_LOCK_TIMEOUT = 200; // 200ms 동안 방향 결정 시간 제공 (더 빠르게 결정)
  
  // 마우스 클릭 처리를 위한 변수 추가
  const clickStartTimeRef = useRef<number>(0);
  const clickPositionRef = useRef<{x: number, y: number}>({x: 0, y: 0});
  const MAX_CLICK_DURATION = 200; // 클릭으로 간주할 최대 시간 (ms)
  const MAX_CLICK_MOVEMENT = 5; // 클릭으로 간주할 최대 이동 거리 (px)

  // 윈도우 리사이즈 이벤트 처리
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobileOrTablet(width <= 1024); // 1024px 이하를 태블릿/모바일로 간주
    };

    // 초기 로드 시 윈도우 너비 설정
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 모바일/태블릿에서 가로 드래그 스크롤 구현
  useEffect(() => {
    if (!isMobileOrTablet || !galleryRef.current) return;
    
    const galleryElement = galleryRef.current;
    
    // 타이머 정리 함수
    const clearDirectionLockTimer = () => {
      if (directionLockTimerRef.current !== null) {
        window.clearTimeout(directionLockTimerRef.current);
        directionLockTimerRef.current = null;
      }
    };
    
    // 관성 스크롤 취소 함수
    const cancelMomentumTracking = () => {
      if (momentumRef.current !== null) {
        cancelAnimationFrame(momentumRef.current);
        momentumRef.current = null;
      }
    };
    
    // 관성 스크롤 (모멘텀 스크롤) 구현
    const momentumScroll = () => {
      if (!galleryElement) return;
      
      // 감속 적용 (0.95는 마찰력, 값이 클수록 느리게 감속)
      velocityRef.current *= 0.95;
      
      // 스크롤 위치 업데이트
      galleryElement.scrollLeft -= velocityRef.current;
      
      // 속도가 충분히 느려지면 스크롤 중지
      if (Math.abs(velocityRef.current) > 0.5) {
        momentumRef.current = requestAnimationFrame(momentumScroll);
      } else {
        cancelMomentumTracking();
      }
    };
    
    // 클릭 가능한 요소 체크 (수정: 카드 자체는 드래그 가능하게)
    const isClickableElement = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return false;
      
      // 특정 클래스를 가진 요소는 드래그 가능하도록 허용
      if (target.closest(`.${styles.projectCard}`)) {
        return false; // 프로젝트 카드는 드래그 가능하게 설정
      }
      
      // 버튼, 입력 필드 등 실제 인터랙션 요소만 클릭으로 처리
      const clickableElements = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
      if (clickableElements.includes(target.tagName)) return true;
      
      // 깊은 중첩 레벨 검사 제거 (카드 내부 요소도 드래그 가능하게)
      return false;
    };
    
    // 드래그 시작 (마우스)
    const handleMouseDown = (e: MouseEvent) => {
      // 클릭 가능한 요소는 드래그로 처리하지 않음
      if (isClickableElement(e.target)) return;
      
      cancelMomentumTracking();
      clearDirectionLockTimer();
      
      velocityRef.current = 0;
      setIsDragging(true);
      setStartX(e.clientX);
      setStartY(e.clientY);
      setDragDirection('none');
      lastDragX.current = e.clientX;
      lastDragTime.current = Date.now();
      setScrollLeft(galleryElement.scrollLeft);
      document.body.style.cursor = 'grabbing';
      
      // 방향 결정 타이머 설정
      directionLockTimerRef.current = window.setTimeout(() => {
        if (dragDirection === 'none') {
          setDragDirection('vertical'); // 기본적으로 수직 스크롤 허용
        }
      }, DIRECTION_LOCK_TIMEOUT);
      
      e.preventDefault();
    };
    
    // 드래그 중 (마우스)
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const x = e.clientX;
      const y = e.clientY;
      const deltaX = x - startX;
      const deltaY = y - startY;
      const now = Date.now();
      
      // 방향이 아직 결정되지 않았고, 충분히 움직였을 때만 방향 결정
      if (dragDirection === 'none') {
        if (Math.abs(deltaX) > DIRECTION_THRESHOLD || Math.abs(deltaY) > DIRECTION_THRESHOLD) {
          clearDirectionLockTimer();
          
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            setDragDirection('horizontal');
            e.preventDefault(); // 수평 드래그로 결정되면 기본 동작 방지
          } else {
            setDragDirection('vertical');
          }
        }
      }
      
      // 수평 드래그일 경우만 갤러리 스크롤 처리
      if (dragDirection === 'horizontal') {
        const timeDelta = Math.max(10, now - lastDragTime.current); // 최소 10ms로 제한
        velocityRef.current = (lastDragX.current - x) / timeDelta * 16.7; // 60fps 기준으로 정규화
        
        lastDragX.current = x;
        lastDragTime.current = now;
        
        const sensitivity = 1.2; // 드래그 감도 약간 증가
        const walk = (startX - x) * sensitivity;
        galleryElement.scrollLeft = scrollLeft + walk;
        e.preventDefault();
      } else if (dragDirection === 'none') {
        // 아직 방향이 결정되지 않았을 때도 미세한 움직임 허용
        if (Math.abs(deltaX) > 1) {
          const walk = (startX - x) * 0.5; // 낮은 감도
          galleryElement.scrollLeft = scrollLeft + walk;
        }
      }
    };
    
    // 드래그 종료 (마우스)
    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      document.body.style.cursor = 'auto';
      clearDirectionLockTimer();
      
      // 수평 드래그였을 때만 관성 스크롤 적용
      if (dragDirection === 'horizontal' && Math.abs(velocityRef.current) > 1) {
        momentumRef.current = requestAnimationFrame(momentumScroll);
      }
      
      // 드래그 방향 초기화는 약간 지연시켜 클릭 이벤트와 충돌 방지
      setTimeout(() => {
        setDragDirection('none');
      }, 50);
    };
    
    // 터치 이벤트 핸들러 (모바일용)
    const handleTouchStart = (e: TouchEvent) => {
      // 클릭 가능한 요소는 드래그로 처리하지 않음
      if (isClickableElement(e.target)) return;
      
      cancelMomentumTracking();
      clearDirectionLockTimer();
      
      velocityRef.current = 0;
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      setStartY(e.touches[0].clientY);
      setDragDirection('none');
      lastDragX.current = e.touches[0].clientX;
      lastDragTime.current = Date.now();
      setScrollLeft(galleryElement.scrollLeft);
      
      // 방향 결정 타이머 설정
      directionLockTimerRef.current = window.setTimeout(() => {
        if (dragDirection === 'none') {
          setDragDirection('vertical'); // 기본적으로 수직 스크롤 허용
        }
      }, DIRECTION_LOCK_TIMEOUT);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const deltaX = x - startX;
      const deltaY = y - startY;
      const now = Date.now();
      
      // 방향이 아직 결정되지 않았고, 충분히 움직였을 때만 방향 결정
      if (dragDirection === 'none') {
        if (Math.abs(deltaX) > DIRECTION_THRESHOLD || Math.abs(deltaY) > DIRECTION_THRESHOLD) {
          clearDirectionLockTimer();
          
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            setDragDirection('horizontal');
            e.preventDefault(); // 수평 드래그로 결정되면 기본 동작 방지
          } else {
            setDragDirection('vertical');
          }
        } else if (Math.abs(deltaX) > 3) {
          // 아직 임계값에 도달하지 않았지만 약간의 수평 움직임 감지 (모바일에서는 더 민감하게)
          e.preventDefault(); // 가로 스와이프가 의도된 것으로 간주하고 기본 동작 방지
        }
      }
      
      // 수평 드래그일 경우만 갤러리 스크롤 처리
      if (dragDirection === 'horizontal') {
        const timeDelta = Math.max(10, now - lastDragTime.current); // 최소 10ms로 제한
        velocityRef.current = (lastDragX.current - x) / timeDelta * 16.7; // 60fps 기준으로 정규화
        
        lastDragX.current = x;
        lastDragTime.current = now;
        
        const sensitivity = 1.2; // 드래그 감도 약간 증가
        const walk = (startX - x) * sensitivity;
        galleryElement.scrollLeft = scrollLeft + walk;
        e.preventDefault();
      } else if (dragDirection === 'none' && Math.abs(deltaX) > 2) {
        // 방향이 결정되지 않았지만 수평 움직임이 있을 때 가벼운 스크롤 허용
        const walk = (startX - x) * 0.8; // 모바일에서는 더 높은 감도
        galleryElement.scrollLeft = scrollLeft + walk;
      }
    };
    
    const handleTouchEnd = () => {
      setIsDragging(false);
      clearDirectionLockTimer();
      
      // 수평 드래그였을 때만 관성 스크롤 적용
      if (dragDirection === 'horizontal' && Math.abs(velocityRef.current) > 1) {
        momentumRef.current = requestAnimationFrame(momentumScroll);
      }
      
      // 드래그 방향 초기화는 약간 지연시켜 터치 이벤트와 충돌 방지
      setTimeout(() => {
        setDragDirection('none');
      }, 50);
    };
    
    // 드래그 취소 (창 벗어남 등)
    const handleDragCancel = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.cursor = 'auto';
        clearDirectionLockTimer();
        setDragDirection('none');
      }
    };
    
    // 이벤트 리스너 등록
    galleryElement.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleDragCancel);
    
    // 터치 이벤트에 옵션 조정
    galleryElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    galleryElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleDragCancel);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      cancelMomentumTracking();
      clearDirectionLockTimer();
      
      galleryElement.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleDragCancel);
      
      galleryElement.removeEventListener('touchstart', handleTouchStart);
      galleryElement.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleDragCancel);
    };
  }, [isDragging, isMobileOrTablet, startX, startY, scrollLeft, dragDirection]);

  // PC에서만 스크롤 위치에 따른 가로 이동 처리
  useEffect(() => {
    if (isMobileOrTablet) return; // 모바일/태블릿에서는 실행하지 않음
    
    const handleScroll = () => {
      if (!sectionRef.current || !galleryRef.current || !titleRef.current) return;
      
      // 페이지 스크롤 위치 가져오기
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      
      // 섹션의 시작과 끝 위치 계산
      const sectionTop = sectionRef.current.offsetTop;
      const sectionHeight = sectionRef.current.offsetHeight;
      
      // 화면 크기에 따라 시작점 조정
      let startOffset = 0.1; // 기본값: 10%
      
      // fixed 영역 시작/끝 지점 설정
      const fixedStartPoint = sectionTop + windowHeight * startOffset;
      const fixedEndPoint = sectionTop + sectionHeight * 0.7;  // 섹션 시작 후 70% 지점
      
      // 컨테이너 상태 업데이트
      if (scrollTop < fixedStartPoint) {
        // 아직 fixed 영역에 도달하지 않음 (상단 영역)
        setContainerState('top');
      } else if (scrollTop >= fixedStartPoint && scrollTop < fixedEndPoint) {
        // fixed 영역 내부
        setContainerState('fixed');
      } else {
        // fixed 영역을 지나침 (하단 영역)
        setContainerState('bottom');
      }
      
      // 갤러리 전체 가로 길이 (스크롤 가능한 최대 거리)
      const galleryWidth = galleryRef.current.scrollWidth;
      const visibleWidth = window.innerWidth;
      
      // PC 화면에서의 패딩 설정
      const sidePadding = 100;
      
      // 조정값을 최소화하여 마지막 카드가 최대한 보이도록 설정
      const scrollEndAdjustment = 60;
      
      const maxScrollDistance = galleryWidth - (visibleWidth - sidePadding) - scrollEndAdjustment;
      
      // 스크롤 진행률 계산 (0~1)
      let progress = 0;
      
      if (containerState === 'fixed') {
        // 고정 상태일 때만 스크롤 진행률 계산
        const totalScrollRange = fixedEndPoint - fixedStartPoint;
        const currentScrollPosition = Math.max(0, Math.min(
          scrollTop - fixedStartPoint,
          totalScrollRange
        ));
        
        progress = currentScrollPosition / totalScrollRange;
      } else if (containerState === 'bottom') {
        // 하단 영역에 도달하면 진행률을 1로 고정
        progress = 1;
      }
      
      // 현재 스크롤 진행률 저장
      scrollProgressRef.current = progress;
      setScrollProgress(progress);
      
      // 갤러리 이동 거리 계산 (0부터 maxScrollDistance까지)
      const translateX = -progress * maxScrollDistance;
      
      // 갤러리 이동 - transform 대신 translate3d로 성능 최적화
      if (galleryRef.current) {
        galleryRef.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
      }
      
      // 타이틀 위치 조정 - 진행률에 따라 타이틀이 완전히 왼쪽으로 사라지도록
      if (titleRef.current) {
        const titleExitPoint = 0.3; // 기본값
        const normalizedProgress = Math.min(progress / titleExitPoint, 1);
        
        // PC 화면에서의 시작 위치
        const startPosition = 50;
        
        // z-index 조정 - 스크롤 진행에 따라 변경
        titleRef.current.style.zIndex = normalizedProgress >= 0.5 ? '0' : '3';
        
        const viewportWidth = window.innerWidth;
        const titleTranslateX = startPosition - (normalizedProgress * (viewportWidth + 300));
        
        titleRef.current.style.transform = `translate3d(${titleTranslateX}px, -50%, 0)`;
        titleRef.current.style.opacity = `${Math.max(0, 1 - normalizedProgress * 1.5)}`; // 투명도를 더 빨리 감소
      }
    };

    // 스크롤 이벤트 리스너 등록 (throttle 적용하여 성능 최적화)
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener);
    // 초기 실행
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [containerState, windowWidth, isMobileOrTablet]);

  // 모바일/태블릿에서 타이틀 위치 조정
  useEffect(() => {
    if (!isMobileOrTablet || !titleRef.current) return;
    
    // 타이틀 위치 및 스타일 리셋
    titleRef.current.style.transform = 'translate3d(50px, -50%, 0)';
    titleRef.current.style.opacity = '1';
    titleRef.current.style.zIndex = '3';
    
  }, [isMobileOrTablet]);

  return (
    <section ref={sectionRef} className={styles.section4}>
      <div 
        ref={containerRef} 
        className={`
          ${styles.container} 
          ${containerState === 'fixed' && !isMobileOrTablet ? styles.fixed : ''} 
          ${containerState === 'bottom' && !isMobileOrTablet ? styles.bottom : ''}
        `}
      >
        <div ref={titleRef} className={styles.title}>
          <h1>Works</h1>
        </div>
        
        <div className={styles.galleryContainer}>
          <div 
            ref={galleryRef} 
            className={`${styles.horizontalGallery} ${isMobileOrTablet ? styles.mobileGallery : ''}`}
          >
            {projectsData.map((project) => (
              <Link 
                key={project.id} 
                to={`/works/${project.slug}`}
                className={styles.projectCard}
              >
                <div className={styles.imageWrapper}>
                  <div className={styles.imagePlaceholder}>
                    {project.imageUrl ? (
                      <img src={project.imageUrl} alt={project.title} />
                    ) : (
                      <span>{project.id}</span>
                    )}
                  </div>
                </div>
                <div className={styles.projectInfo}>
                  <h3 className={styles.projectTitle}>{project.title}</h3>
                  {/* <div className={styles.projectMeta}>
                    <span className={styles.projectCategory}>{project.category}</span>
                    <span className={styles.projectYear}>{project.year}</span>
                  </div> */}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      {/* 고정 컨테이너를 위한 공간 확보 (fixed 상태일 때 일반 흐름에서 빠지므로) */}
      <div className={styles.spacer} style={{ 
        height: !isMobileOrTablet && containerState === 'fixed' ? '100vh' : '0' 
      }} />
    </section>
  );
};

export default Section4;